<template>
  <div>
    <div class="navigation">
      <div class="navigation_bottom">
        <img class="ai_assistant" ref="ai_assistant"
          src="https://mj-uploads.oss-cn-shenzhen.aliyuncs.com/public/aiDesign/web/AI_Ass/AI_Ass.png" alt="">
        <span ref="scroll_bottom" class="scroll_bottom iconfont" @click="scrollToBottom">&#xe685; 回到底部</span>
      </div>
      <div class="navigation_items" ref="navigation_items">
        <div class="main_input" ref="main_input"
          @dragover.prevent="handleDragOver"
          @dragleave.prevent="handleDragLeave"
          @drop.prevent="handleDrop"
          :class="{ 'drag-over': isDragging }">
          <!-- 已上传图片缩略图区域（最多 14 张） -->
          <div v-if="uploadImages.length > 0" class="uploaded-thumbnails">
            <div v-for="(img, idx) in uploadImages" :key="idx" class="thumbnail-item">
              <img :src="img" alt="上传图片" class="thumbnail-img">
              <div class="remove-btn" @click.stop="removeImage(idx)">×</div>
            </div>
          </div>

          <!-- 输入框区域 -->
          <div class="input-area">
            <textarea name="" id="" placeholder="描述你想要的精修效果..."
              v-model="textarea"
              @focus="handleTextareaFocus"
              @input="handleTextareaInput"
              @blur="handleTextareaBlur"
              @click="handleTextareaClick"
              @keydown="handleTextareaKeydown"
              :disabled="processing"></textarea>
            <button ref="up" class="sent iconfont" :class="{ 'has_input': canSend }" @click.stop="startTask" v-show="!scrollStatus" :disabled="!canSend || processing">
              <span v-if="!processing">&#xe688;</span>
              <span v-else class="rotating">&#xe6a4;</span>
            </button>
          </div>

          <!-- 底部功能按钮行位于输入框下方 -->
          <div class="input-options" @click.stop>
            <!-- 上传按钮 -->
            <div class="option-btn upload-btn" @click.stop="handleImageUpload">
              <span>上传</span>
            </div>
            <!-- 图像大小下拉 -->
            <div class="option-dropdown" @click.stop="toggleResolutionMenu">
              <span class="option-label">图像大小</span>
              <span class="option-value">{{ selectedResolution }}</span>
              <span class="iconfont arrow"></span>
              <div v-show="showResolutionMenu" class="dropdown-menu" @click.stop>
                <div
                  v-for="resolution in resolutionOptions"
                  :key="resolution"
                  class="menu-item"
                  :class="{ active: selectedResolution === resolution }"
                  @click="selectResolution(resolution)"
                >
                  {{ resolution }}
                </div>
              </div>
            </div>
            <!-- 模型选择下拉 -->
            <div class="option-dropdown" @click.stop="toggleModelMenu">
              <span class="option-label">模型</span>
              <span class="option-value">{{ selectedModel }}</span>
              <span class="iconfont arrow"></span>
              <div v-show="showModelMenu" class="dropdown-menu" @click.stop>
                <div
                  v-for="model in modelOptions"
                  :key="model"
                  class="menu-item"
                  :class="{ active: selectedModel === model }"
                  @click="selectModel(model)"
                >
                  {{ model }}
                </div>
              </div>
            </div>
            <!-- 高宽比下拉（nano-banana-pro 以外可见，紧跟模型选项之后） -->
            <div v-if="selectedModel !== 'nano-banana-pro'" class="option-dropdown" @click.stop="toggleAspectRatioMenu">
              <span class="option-label">高宽比</span>
              <span class="option-value">{{ selectedAspectRatio }}</span>
              <span class="iconfont arrow"></span>
              <div v-show="showAspectRatioMenu" class="dropdown-menu" @click.stop>
                <div
                  v-for="ratio in aspectRatioOptions"
                  :key="ratio"
                  class="menu-item"
                  :class="{ active: selectedAspectRatio === ratio }"
                  @click="selectAspectRatio(ratio)"
                >
                  {{ ratio }}
                </div>
              </div>
            </div>
          </div>
        </div>
        <!-- 放大后的发送按钮（绝对定位） -->
        <button ref="down" class="sent_expanded iconfont" :class="{ 'has_input': canSend }" @click.stop="startTask" v-show="scrollStatus" :disabled="!canSend || processing">
          <span v-if="!processing">&#xe688;</span>
          <span v-else class="rotating">&#xe6a4;</span>
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import { Message } from 'element-ui'
import { getOssClient } from '@/utils/oss'
import { v4 as uuidv4 } from 'uuid'
import { genPrompt } from '@/api/prompt'
import { genOssImage } from '@/utils/ossPath'
import { getLastUsedBaseUrl, getWebSocketUrl, cudaIndexNum } from '@/utils/comfyUiRequest'
import axios from 'axios'
import cuda from '@/utils/checkCuda'
import productWorkflow from '@/api/json_api/产品精修.json'
import productWorkflowSpecial from '@/api/json_api/产品精修_特殊类.json'
import productWorkflowGPT25 from '@/api/json_api/产品精修_GPT2.5.json'
import { getTaskEnd, getTaskStart } from '@/api/user'
import { Product_Retouch } from '@/enums/common/modules'
import { getImageSizeByLoading } from '@/utils/helper/function'

import { OSS_ACCESS_KEY_ID, OSS_ACCESS_KEY_SECRET } from '@/utils/secretCrypto'
// 模型下拉可选项（页面：多模态ai修图/生图）
// nano-banana-pro → 产品精修.json；gpt-image-2-vip / 场景类 → 产品精修_特殊类.json；
// gpt-image-2.5-sunburst / gpt-image-2.5-flare → 产品精修_GPT2.5.json（按 model 字段区分）
const MODEL_OPTIONS = ['nano-banana-pro', 'gpt-image-2-vip', 'gpt-image-2.5-sunburst', 'gpt-image-2.5-flare', '场景类']

// 旧「精修类型」值 → 新模型名的兼容映射（用于历史对话回填）
const LEGACY_TYPE_TO_MODEL = {
  常规类: 'nano-banana-pro',
  特殊类: 'gpt-image-2-vip',
  场景类: '场景类'
}

// 各工作流的 OSS 上传节点 ID，用于 fetchResultImage 读取输出 filename（仅作 fallback，实际以 config.ossNodeId 为准）
const OSS_NODE_ID_MAP = {
  'nano-banana-pro': '8',
  'gpt-image-2-vip': '7',
  'gpt-image-2.5-sunburst': '7',
  'gpt-image-2.5-flare': '7',
  场景类: '7'
}

// 常规类（BananaNode）动态生成的图像加载节点 ID 序列（跳过已被 prompt/seed/OSS 占用的 5/6/8）
// 对应输入顺序：image1 -> 7, image2 -> 9, image3 -> 10, ..., image14 -> 21
const REGULAR_LOADER_NODE_IDS = ['7', '9', '10', '11', '12', '13', '14', '15', '16', '17', '18', '19', '20', '21']

// 特殊类工作流中动态生成的图像加载节点 ID 序列（跳过被OSS占用的 7）
// 对应输入顺序：image_01 -> 6, image_02 -> 8, image_03 -> 9, ..., image_14 -> 20
const SPECIAL_LOADER_NODE_IDS = ['6', '8', '9', '10', '11', '12', '13', '14', '15', '16', '17', '18', '19', '20']

// 特殊类分辨率映射（UI 上的 1K/2K/4K → GPT-image-2-vip 节点期望的字符串）
const SPECIAL_RESOLUTION_MAP = {
  '1K': '1K Fast',
  '2K': '2K Recommended',
  '4K': '4K Detail'
}

// 特殊类高宽比可选项（默认 16:9）
const SPECIAL_ASPECT_RATIO_OPTIONS = ['1:1', '2:3', '3:2', '3:4', '4:3', '4:5', '5:4', '9:16', '16:9', '21:9']

// 场景类默认提示词模板
const SCENE_CLASS_DEFAULT_PROMPT = `产品类型：（如甲油胶、指甲油、延长胶、水晶粉等）
产品名称：（该产品的品牌名、商品名）
核心卖点：（该产品的特点，也可以输入简单词汇让ai自行补充）
风格倾向：（期望的图片风格，可输入多个形容词以获得精准风格图片）`

// 场景类 GPT-images-2 提示词模板（PROMPT 占位符会被替换为用户输入）
const SCENE_CLASS_GPT_PROMPT_TEMPLATE = `---
name: amazon-aplus-nail-multiscreen
description: 为美甲类目生成一张亚马逊高级 A+ 连屏套图（6屏无缝长图）。
version: 2.3
category: image-generation
domain: amazon-a-plus / nail-category / multi-screen
---

# 亚马逊高级 A+ 连屏套图生成（美甲专用）

你是一名亚马逊视觉设计师。请严格基于下方的“产品输入”和“参考图”，直接生成**一张**符合全部硬性要求的连屏套图。**只输出图片，不输出任何额外文字、解释或提示词。**

## 产品输入
{{PROMPT}}

## 参考图（最高优先级）
（此处将自动传入用户上传的参考图）

参考图是你进行设计的**绝对基础**，必须遵守以下规则：
- **视觉还原优先**：整张长图的色调、光影质感、产品气质、构图骨架、字体风格，都必须**直接从参考图提取并高度还原**，不得脱离参考图自行设计风格。
- **连屏扩展方式**：将参考图视为第一屏的视觉起点，沿其风格自然延伸出后续5屏，形成一张连续长卷。
- **冲突裁决**：若本文件其他规范与参考图的核心视觉特征发生冲突，**以参考图为准**。参考图是唯一的设计方向来源。

## 图片硬性定义（拼接零容忍）
这是一张连续长卷，绝对禁止任何拼接痕迹。以下必须全部满足：
- **强制跨屏贯穿**：必须有至少一个视觉元素（产品、液体流动弧线、色带、光影或纹理）从第一屏一直延伸到第六屏，或连续跨越多个屏，形成不间断的视觉桥梁。
- **背景绝对统一**：全图背景必须是一个平缓渐变或同一轻纹理，禁止任何色块切换、重新开始纯色或明显断层。
- **零边界**：屏与屏之间不得有任何水平线、缝隙、边框、色卡分隔或等高模块轮廓。
- **版式强制偏移**：每一屏的产品或视觉重心必须较前一屏左右偏移，形成锯齿形或波浪形节奏，绝不允许上下垂直对齐。

## 6屏内容固定顺序（不可调整）
1. **首屏（视觉锤）**：品牌气质 + 产品主体，完全延续参考图的构图方式。
2. **核心卖点一**：持久度、上色效果。
3. **质地展示**：凝胶/液态流动感、通透光泽，参考图中的质感特征必须在此屏复现。
4. **使用效果**：上甲后效果，只展示手部特写，无面部/全身。
5. **卖点二/对比**：易用性、安全性、前后对比。
6. **收尾（信任锚）**：品牌氛围、卖点小结。

## 美甲类目强制表达
必须明确呈现至少一种产品特征：高光泽、通透感、液态/凝胶/粉末质地、指甲成品效果。手部局部可出镜，完整人物或脸部绝对禁止。

## 视觉风格唯一指定
- 干净、高级、电商感强，非海报、非社媒、非插画。
- 色彩与质感严格跟随参考图，无自主发挥。

## 文案硬性规范（全英文，面向欧美年轻女性）
- 全部文案必须英文，字体家族从参考图中提取或使用与其气质匹配的统一字体。
- 每一屏至少包含一个核心层级，文案位置错落分布，禁止连续居中或重复同一种对齐方式，但不得遮挡产品主体，必要时可加半透明底衬。

| 层级 | 要求 | 视觉 |
|------|------|------|
| Headline（主标题） | 3–7词 | 粗体、大字号 |
| Sub-headline（副标题） | ≤12词 | 常规字重，小于主标题 |
| Tag/Badge（标签） | 1–3词 | 全大写/首字母大写，可配色块 |
| Caption（注释） | ≤8词 | 小字号、辅助色 |

禁止中文、长段落、夸张绝对化用语。

## 绝对禁止
- 完整人物、脸部、全身、生活场景。
- 任何6个独立模块的拼接感或等高分块痕迹。
- 任何超出参考图许可的色彩或风格自主改变。

## 交付（无自主空间）
直接生成一张符合以上全部硬性标准的长图。宽度固定为1460px，总高度根据内容自适应但须保证六屏节奏完整，分辨率150dpi。不得自行变更比例或尺寸策略。`

// 各模型的统一配置：工作流模板 / OSS 节点 ID / 动态加载节点序列 / 图像输入键格式 / 分辨率占位符与映射 / seed 节点 ID / model 字段
const WORKFLOW_CONFIG = {
  'nano-banana-pro': {
    workflow: productWorkflow,
    ossNodeId: '8',
    loaderNodeIds: REGULAR_LOADER_NODE_IDS,
    imageKey: (i) => `image${i + 1}`,
    resolutionPlaceholder: '{{RESOLUTION}}',
    resolutionMap: null,
    seedNodeId: '6'
  },
  'gpt-image-2-vip': {
    workflow: productWorkflowSpecial,
    ossNodeId: '7',
    loaderNodeIds: SPECIAL_LOADER_NODE_IDS,
    imageKey: (i) => `image_${String(i + 1).padStart(2, '0')}`,
    resolutionPlaceholder: '{{RESOLUTION_VIP}}',
    resolutionMap: SPECIAL_RESOLUTION_MAP,
    seedNodeId: '5'
  },
  'gpt-image-2.5-sunburst': {
    workflow: productWorkflowGPT25,
    ossNodeId: '7',
    loaderNodeIds: SPECIAL_LOADER_NODE_IDS,
    imageKey: (i) => `image_${String(i + 1).padStart(2, '0')}`,
    resolutionPlaceholder: '{{RESOLUTION}}',
    resolutionMap: null,
    seedNodeId: '5',
    model: 'gpt-image-2.5-sunburst'
  },
  'gpt-image-2.5-flare': {
    workflow: productWorkflowGPT25,
    ossNodeId: '7',
    loaderNodeIds: SPECIAL_LOADER_NODE_IDS,
    imageKey: (i) => `image_${String(i + 1).padStart(2, '0')}`,
    resolutionPlaceholder: '{{RESOLUTION}}',
    resolutionMap: null,
    seedNodeId: '5',
    model: 'gpt-image-2.5-flare'
  },
  场景类: {
    workflow: productWorkflowSpecial,
    ossNodeId: '7',
    loaderNodeIds: SPECIAL_LOADER_NODE_IDS,
    imageKey: (i) => `image_${String(i + 1).padStart(2, '0')}`,
    resolutionPlaceholder: '{{RESOLUTION_VIP}}',
    resolutionMap: SPECIAL_RESOLUTION_MAP,
    seedNodeId: '5',
    promptTemplate: SCENE_CLASS_GPT_PROMPT_TEMPLATE
  }
}

export default {
  name: 'NaviGation_Product',
  props: {
    initialChatHistory: {
      type: Array,
      default: () => []
    }
  },
  data() {
    return {
      textarea: '',
      uploadImages: [], // 上传的图片 URL 数组（最多 maxImages 张）
      maxImages: 14, // 最大上传数量
      processing: false,
      productChatHistory: [],
      scrollStatus: false,
      // 图像大小选项
      selectedResolution: '2K',
      resolutionOptions: ['1K', '2K', '4K'],
      showResolutionMenu: false,
      // 高宽比选项（nano-banana-pro 以外生效，默认 16:9）
      selectedAspectRatio: '16:9',
      aspectRatioOptions: SPECIAL_ASPECT_RATIO_OPTIONS,
      showAspectRatioMenu: false,
      // 模型选择（下拉菜单）
      selectedModel: 'nano-banana-pro',
      modelOptions: MODEL_OPTIONS,
      showModelMenu: false,
      // 拖拽状态
      isDragging: false,
      // WebSocket
      ws: null,
      clientId: '',
      // 防止重复获取图片标志
      imageFetched: false,
      // 任务日志ID
      taskLogId: null,
      // OSS 配置
      ossConfig: {
        accessKeyId: OSS_ACCESS_KEY_ID,
        accessKeySecret: OSS_ACCESS_KEY_SECRET
      },
      // 节点名称映射
      nodeNameMap: {
        1: 'In AI image generation',
        5: 'Analyze prompt',
        6: 'Generate random seed',
        7: 'Load product image',
        8: 'Upload generated results'
      },
      currentExecutingNode: '' // 当前执行的节点名称
    }
  },
  computed: {
    canSend() {
      return this.textarea.trim() !== '' && this.uploadImages.length > 0
    }
  },
  created() {
    if (this.initialChatHistory && this.initialChatHistory.length > 0) {
      this.productChatHistory = [...this.initialChatHistory]
    }
    // 从 sessionStorage 读取传递的提示词
    const savedPrompt = sessionStorage.getItem('product_refine_prompt')
    if (savedPrompt) {
      // 延迟设置提示词，确保组件已挂载
      this.$nextTick(() => {
        this.textarea = savedPrompt
        console.log('从提示词优化页面传入的提示词:', savedPrompt)
        // 清除 sessionStorage 中的数据，避免下次进入时重复显示
        sessionStorage.removeItem('product_refine_prompt')
      })
    }
  },
  mounted() {
    // 监听 paste 事件实现 Ctrl+V 粘贴图片（之前用 keydown 无法获取 clipboardData）
    window.addEventListener('paste', this.handlePaste)
    // 点击外部关闭下拉菜单
    document.addEventListener('click', this.closeAllMenus)
    // 监听重新生成事件
    this.$root.$on('product-regenerate', this.handleRegenerateFromParent)
    // 监听重新填入事件
    this.$root.$on('product-refill', this.handleRefillFromParent)
  },
  beforeDestroy() {
    window.removeEventListener('paste', this.handlePaste)
    document.removeEventListener('click', this.closeAllMenus)
    // 移除事件监听
    this.$root.$off('product-regenerate', this.handleRegenerateFromParent)
    this.$root.$off('product-refill', this.handleRefillFromParent)
  },
  methods: {
    // 处理图片上传（支持多选，最多 maxImages 张）
    handleImageUpload() {
      if (this.processing) return
      const remaining = this.maxImages - this.uploadImages.length
      if (remaining <= 0) {
        Message.warning(`最多只能上传${this.maxImages}张图片!`)
        return
      }
      const input = document.createElement('input')
      input.type = 'file'
      input.accept = 'image/*'
      input.multiple = true
      input.onchange = async (event) => {
        const files = Array.from(event.target.files)
        if (files.length === 0) return
        const toUpload = files.slice(0, remaining)
        if (files.length > remaining) {
          Message.warning(`最多还能上传${remaining}张图片，已自动截取前${remaining}张`)
        }
        for (const file of toUpload) {
          await this.processImage(file)
        }
      }
      input.click()
    },

    // 处理图片文件
    async processImage(file) {
      if (this.uploadImages.length >= this.maxImages) {
        Message.warning(`最多只能上传${this.maxImages}张图片!`)
        return
      }
      try {
        Message.info('正在上传图片...')
        const ossClient = await getOssClient()
        const tmp = '/input/'
        const fileName = `product_${Date.now()}_${file.name}`
        const result = await ossClient.put(tmp + fileName, file)

        console.log('OSS上传结果:', result)

        if (result.name) {
          // 使用 result.name 构建 ComfyUI 可访问的 URL
          const imageUrl = `https://mj-comfy-ui.oss-cn-shenzhen.aliyuncs.com/${result.name}`
          console.log('生成的图片URL:', imageUrl)

          this.uploadImages.push(imageUrl)
          Message.success('图片上传成功!')
        } else {
          Message.error('图片上传失败，请重试!')
        }
      } catch (error) {
        console.error('上传图片失败:', error)
        Message.error('图片上传失败，请检查网络!')
      }
    },

    // 移除图片（按索引）
    removeImage(index) {
      this.uploadImages.splice(index, 1)
    },

    // 拖拽上传相关
    handleDragOver() {
      this.isDragging = true
    },
    handleDragLeave() {
      this.isDragging = false
    },
    async handleDrop(event) {
      this.isDragging = false
      const files = Array.from(event.dataTransfer.files || [])
      if (files.length === 0) return
      const remaining = this.maxImages - this.uploadImages.length
      if (remaining <= 0) {
        Message.warning(`最多只能上传${this.maxImages}张图片!`)
        return
      }
      const imageFiles = files.filter(f => f.type && f.type.startsWith('image/'))
      if (imageFiles.length === 0) {
        Message.warning('请拖入图片文件!')
        return
      }
      const toUpload = imageFiles.slice(0, remaining)
      if (imageFiles.length > remaining) {
        Message.warning(`最多还能上传${remaining}张图片，已自动截取前${remaining}张`)
      }
      for (const file of toUpload) {
        await this.processImage(file)
      }
    },

    // 切换图像大小菜单
    toggleResolutionMenu() {
      this.showResolutionMenu = !this.showResolutionMenu
      this.showAspectRatioMenu = false
      this.showModelMenu = false
    },

    // 选择图像大小
    selectResolution(resolution) {
      this.selectedResolution = resolution
      this.showResolutionMenu = false
    },

    // 切换高宽比菜单（nano-banana-pro 以外使用）
    toggleAspectRatioMenu() {
      this.showAspectRatioMenu = !this.showAspectRatioMenu
      this.showResolutionMenu = false
      this.showModelMenu = false
    },

    // 选择高宽比
    selectAspectRatio(ratio) {
      this.selectedAspectRatio = ratio
      this.showAspectRatioMenu = false
    },

    // 切换模型选择菜单
    toggleModelMenu() {
      this.showModelMenu = !this.showModelMenu
      this.showResolutionMenu = false
      this.showAspectRatioMenu = false
    },

    // 选择模型（nano-banana-pro / gpt-image-2-vip / gpt-image-2.5-sunburst / gpt-image-2.5-flare / 场景类）
    selectModel(model) {
      this.selectedModel = model
      this.showModelMenu = false
      // 场景类自动填入默认提示词模板
      if (model === '场景类') {
        this.textarea = SCENE_CLASS_DEFAULT_PROMPT
      }
    },

    // 动态构建工作流：根据上传图片数量动态添加加载节点与 image_N 连线，支持常规类/特殊类
    buildDynamicWorkflow(type, images, prompt, seed, resolution, aspectRatio) {
      const config = WORKFLOW_CONFIG[type]
      if (!config) {
        throw new Error(`未知的精修类型: ${type}`)
      }
      if (!config.workflow) {
        throw new Error(`${type} 工作流未加载`)
      }
      if (!Array.isArray(images) || images.length === 0) {
        throw new Error(`${type} 工作流需要至少 1 张参考图`)
      }
      const max = config.loaderNodeIds.length // 14
      if (images.length > max) {
        throw new Error(`${type} 工作流最多支持 ${max} 张参考图`)
      }

      const workflow = JSON.parse(JSON.stringify(config.workflow))
      console.log(`🧩 构建 [${type}] 工作流，参考图数量: ${images.length}`)

      // 1) 为每张图动态创建 AC_从URL加载图像 节点，并在节点 1 上添加 imageN 连线
      images.forEach((url, i) => {
        const nodeId = config.loaderNodeIds[i]
        workflow[nodeId] = {
          inputs: {
            image_url: url
          },
          class_type: 'AC_从URL加载图像',
          _meta: {
            title: `参考图 ${i + 1}`
          }
        }
        const inputKey = config.imageKey(i)
        workflow['1'].inputs[inputKey] = [nodeId, 0]
        console.log(`  └─ 节点 ${nodeId} → ${inputKey} (${url})`)
      })

      // 2) 替换静态变量（PROMPT / SEED / RESOLUTION / OUTPUT_FILENAME / OSS_*）
      const timestamp = Date.now()
      const randomNum = Math.floor(Math.random() * 99999)
      const outputFilename = `["tmp-comfyui/product/${timestamp}_${randomNum}.png"]`
      const resolutionValue = config.resolutionMap
        ? (config.resolutionMap[resolution] || config.resolutionMap['2K'] || resolution)
        : resolution
      // 场景类使用提示词模板，将用户输入填入 {{PROMPT}} 占位符
      const actualPrompt = config.promptTemplate
        ? config.promptTemplate.replace('{{PROMPT}}', prompt)
        : prompt

      for (const nodeId in workflow) {
        const node = workflow[nodeId]
        if (!node || !node.inputs) continue
        for (const inputKey in node.inputs) {
          const inputValue = node.inputs[inputKey]
          if (typeof inputValue !== 'string') continue
          if (inputValue.includes('{{SEED}}')) {
            node.inputs[inputKey] = String(seed)
          } else if (inputValue.includes('{{PROMPT}}')) {
            node.inputs[inputKey] = actualPrompt
          } else if (inputValue.includes(config.resolutionPlaceholder)) {
            node.inputs[inputKey] = resolutionValue
          } else if (inputValue.includes('{{ASPECT_RATIO}}')) {
            node.inputs[inputKey] = aspectRatio || '16:9'
          } else if (inputValue.includes('{{MODEL}}')) {
            node.inputs[inputKey] = config.model || inputValue
          } else if (inputValue.includes('{{OUTPUT_FILENAME}}')) {
            node.inputs[inputKey] = outputFilename
          } else if (inputValue.includes('{{OSS_ACCESS_KEY_ID}}')) {
            node.inputs[inputKey] = this.ossConfig.accessKeyId
          } else if (inputValue.includes('{{OSS_ACCESS_KEY_SECRET}}')) {
            node.inputs[inputKey] = this.ossConfig.accessKeySecret
          }
        }
      }

      // 3) 把 seed 节点的 seed 字段转回 Number，避免 ComfyUI 校验失败
      const seedNode = workflow[config.seedNodeId]
      if (seedNode && seedNode.inputs && typeof seedNode.inputs.seed === 'string') {
        const seedNum = Number(seedNode.inputs.seed)
        if (!Number.isNaN(seedNum)) {
          seedNode.inputs.seed = seedNum
        }
      }

      console.log(`🔍 [${type}] 工作流构建完成:`, JSON.stringify(workflow, null, 2))
      return workflow
    },
    // 关闭所有下拉菜单
    closeAllMenus() {
      this.showResolutionMenu = false
      this.showAspectRatioMenu = false
      this.showModelMenu = false
    },

    // 处理 textarea 回车发送
    handleTextareaKeydown(event) {
      if (event.key === 'Enter' && !event.shiftKey) {
        event.preventDefault()
        this.startTask()
      }
    },
    // 处理 textarea 获得焦点
    handleTextareaFocus() {
      // 确保导航框处于放大状态
      if (!this.scrollStatus) {
        this.handleScroll(true)
      }
    },
    // 处理 textarea 输入
    handleTextareaInput() {
      // 用户输入时保持放大状态
      if (!this.scrollStatus) {
        this.handleScroll(true)
      }
    },
    // 处理 textarea 失去焦点
    handleTextareaBlur() {
      // 不做任何处理，保持当前状态
    },
    // 处理 textarea 点击
    handleTextareaClick() {
      // 点击输入框时放大
      if (!this.scrollStatus) {
        this.handleScroll(true)
      }
    },
    // 导航栏状态
    handleScroll(val) {
      if (val) {
        // 放大状态
        if (this.$refs.navigation_items) {
          this.$refs.navigation_items.style.width = '70%'
        }
        if (this.$refs.ai_assistant) this.$refs.ai_assistant.style.opacity = 1
        if (this.$refs.scroll_bottom) this.$refs.scroll_bottom.style.opacity = 1
        this.scrollStatus = true
      } else {
        // 缩小状态
        if (this.$refs.navigation_items) {
          this.$refs.navigation_items.style.width = '60%'
        }
        if (this.$refs.ai_assistant) this.$refs.ai_assistant.style.opacity = 0
        if (this.$refs.scroll_bottom) this.$refs.scroll_bottom.style.opacity = 0
        this.scrollStatus = false
      }
    },
    // 回滚到底部
    scrollToBottom() {
      if (this.scrollStatus) {
        this.$emit('scrollToBottom')
      }
    },

    // Ctrl+V 粘贴图片（支持多图，遵循 maxImages 数量限制和 processing 状态）
    async handlePaste(event) {
      // 正在处理中不接收新粘贴
      if (this.processing) return
      try {
        const clipboardData = event.clipboardData || window.clipboardData
        if (!clipboardData) return
        const items = clipboardData.items
        if (!items || items.length === 0) return

        // 收集剪贴板中的所有图片文件
        const imageFiles = []
        for (let i = 0; i < items.length; i++) {
          const item = items[i]
          if (item.kind === 'file' && item.type && item.type.indexOf('image') !== -1) {
            const blob = item.getAsFile()
            if (blob) imageFiles.push(blob)
          }
        }
        if (imageFiles.length === 0) return

        // 阻止默认粘贴行为（避免图片文件名等被插入 textarea）
        event.preventDefault()

        // 根据剩余可上传数量截取
        const remaining = this.maxImages - this.uploadImages.length
        if (remaining <= 0) {
          Message.warning(`最多只能上传${this.maxImages}张图片!`)
          return
        }
        const toUpload = imageFiles.slice(0, remaining)
        if (imageFiles.length > remaining) {
          Message.warning(`最多还能上传${remaining}张图片，已自动截取前${remaining}张`)
        }

        // 确保粘贴时输入框处于放大状态
        if (!this.scrollStatus) {
          this.handleScroll(true)
        }

        for (const file of toUpload) {
          await this.processImage(file)
        }
      } catch (error) {
        console.error('粘贴图片失败:', error)
        Message.error('粘贴图片失败，请重试!')
      }
    },

    // 处理来自父组件的重新生成请求
    handleRegenerateFromParent(regenerateData) {
      if (!regenerateData) return
      console.log('接收到重新生成请求:', regenerateData)
      // 填充输入框和图片（兼容单图字符串与多图数组）
      this.textarea = regenerateData.content
      if (Array.isArray(regenerateData.images)) {
        this.uploadImages = regenerateData.images.slice(0, this.maxImages)
      } else if (regenerateData.image) {
        this.uploadImages = [regenerateData.image]
      } else {
        this.uploadImages = []
      }
      this.selectedResolution = regenerateData.resolution
      if (regenerateData.type) {
        // 将旧「精修类型」值归一到新模型名
        this.selectedModel = LEGACY_TYPE_TO_MODEL[regenerateData.type] || regenerateData.type
      }
      // 高宽比仅在非 nano-banana-pro 下回填，其他情况保持当前值
      if (this.selectedModel !== 'nano-banana-pro' && regenerateData.aspectRatio) {
        this.selectedAspectRatio = regenerateData.aspectRatio
      }
      // 清除 sessionStorage
      sessionStorage.removeItem('product_regenerate')
      // 自动触发生成
      this.$nextTick(() => {
        this.startTask()
      })
    },

    // 处理来自父组件的重新填入请求（仅回填，不自动生成）
    handleRefillFromParent(refillData) {
      if (!refillData) return
      console.log('接收到重新填入请求:', refillData)
      this.textarea = refillData.content
      if (Array.isArray(refillData.images)) {
        this.uploadImages = refillData.images.slice(0, this.maxImages)
      } else if (refillData.image) {
        this.uploadImages = [refillData.image]
      } else {
        this.uploadImages = []
      }
      this.selectedResolution = refillData.resolution || '2K'
      // 将旧「精修类型」值归一到新模型名
      this.selectedModel = LEGACY_TYPE_TO_MODEL[refillData.type] || refillData.type || 'nano-banana-pro'
      // 高宽比仅在非 nano-banana-pro 下回填，其他情况保持当前值
      if (this.selectedModel !== 'nano-banana-pro' && refillData.aspectRatio) {
        this.selectedAspectRatio = refillData.aspectRatio
      }
      // 确保导航框放大显示
      if (!this.scrollStatus) {
        this.handleScroll(true)
      }
    },

    // 开始任务：常规类与特殊类均一次任务多图（最多 14 张）
    async startTask() {
      if (!this.canSend || this.processing) {
        return
      }

      // 确保放大状态
      if (!this.scrollStatus) {
        this.handleScroll(true)
      }

      if (!this.uploadImages || this.uploadImages.length === 0) {
        Message.warning('请先上传图片!')
        return
      }

      if (this.textarea.trim() === '') {
        Message.warning('请输入精修需求!')
        return
      }

      const userMessage = this.textarea
      const userImages = [...this.uploadImages]
      const userResolution = this.selectedResolution
      const userType = this.selectedModel
      // 高宽比仅 nano-banana-pro 以外传递，nano-banana-pro 为 null
      const userAspectRatio = (userType !== 'nano-banana-pro') ? this.selectedAspectRatio : null

      // 校验对应工作流是否已加载
      const config = WORKFLOW_CONFIG[userType]
      if (!config || !config.workflow) {
        Message.error(`${userType || '当前'} 工作流尚未配置，请联系管理员`)
        return
      }

      // 清空输入
      this.textarea = ''
      this.uploadImages = []

      this.processing = true
      this.$emit('aiTyping', true)

      // 清空 textarea 后立即强制保持放大状态
      this.$nextTick(() => {
        this.handleScroll(true)
      })

      console.log('🧩 当前精修类型:', userType)
      console.log(`📦 待处理参考图数量: ${userImages.length}`)

      await this.runDynamicTask(userType, userImages, userMessage, userResolution, userAspectRatio)

      this.processing = false
      this.$emit('aiTyping', false)
      console.log(`🎉 [${userType}] 任务处理完毕`)
    },
    // 通用动态任务：一次提交包含多张参考图的工作流，适用于常规类与特殊类
    async runDynamicTask(userType, userImages, userMessage, userResolution, userAspectRatio) {
      const config = WORKFLOW_CONFIG[userType]
      if (!config) {
        Message.error(`未知的精修类型: ${userType}`)
        return
      }

      // 推一条用户消息：image 字段存首张作为缩略图，images 数组包含全部参考图
      const displayContent = userImages.length > 1
        ? `${userMessage}（共 ${userImages.length} 张参考图）`
        : userMessage
      this.productChatHistory.push({
        role: 'user',
        content: displayContent,
        image: userImages[0],
        images: [...userImages],
        resolution: userResolution,
        aspectRatio: userAspectRatio,
        type: userType,
        timestamp: new Date().toISOString()
      })
      this.$emit('chatUpdate', this.productChatHistory)
      this.$emit('aiTyping', true)
      this.processing = true
      // 记录读取输出的 OSS 节点 ID（常规类 8，特殊类 7）
      this._currentOssNodeId = config.ossNodeId || OSS_NODE_ID_MAP[userType]

      try {
        const seed = Math.floor(Math.random() * 100000000000000)
        const mappedResolution = config.resolutionMap
          ? (config.resolutionMap[userResolution] || userResolution)
          : userResolution
        console.log(`🎲 [${userType}] 生成的种子:`, seed)
        console.log(`📝 [${userType}] 提示词:`, userMessage)
        console.log(`📺 [${userType}] 图像大小:`, userResolution, '→', mappedResolution)
        console.log(`🖼️ [${userType}] 参考图数量: ${userImages.length}`)

        // 动态构建工作流
        const promptData = this.buildDynamicWorkflow(
          userType,
          userImages,
          userMessage,
          seed,
          userResolution,
          userAspectRatio
        )

        this.clientId = uuidv4()
        const data = {
          prompt: promptData,
          client_id: this.clientId
        }

        console.log(`🚀 [${userType}] 准备发送任务...`)
        console.log(`📦 [${userType}] 客户端ID:`, this.clientId)

        const result = await genPrompt(data)
        console.log(`📨 [${userType}] genPrompt 返回结果:`, result)

        if (result && result.prompt_id) {
          console.log(`✅ [${userType}] 任务提交成功，promptId:`, result.prompt_id)
          await this.listenToProductProgress(result.prompt_id, userMessage, userImages)
        } else {
          throw new Error('任务提交失败')
        }
      } catch (error) {
        console.error(`❗ [${userType}] 产品精修失败:`, error)
        let errorMessage = '产品精修失败，请稍后再试!'
        if (error.response) {
          console.error('⚠️ 服务器返回错误:', error.response.status)
          errorMessage = `服务器错误: ${error.response.status}`
        } else if (error.message) {
          errorMessage = error.message
        }
        this.productChatHistory.push({
          role: 'assistant',
          content: '抱歉，似乎发生了某些问题',
          timestamp: new Date().toISOString(),
          isError: true
        })
        this.$emit('chatUpdate', this.productChatHistory)
        Message.error(errorMessage)
      }
    },
    // 监听产品精修进度（返回 Promise，任务完成/失败/超时均会 resolve）
    listenToProductProgress(promptId, taskPrompt, taskImages) {
      return new Promise((resolve) => {
        // 使用一次性 resolver，防止重复 resolve
        let settled = false
        const finishTask = () => {
          if (settled) return
          settled = true
          this._resolveCurrentTask = null
          resolve()
        }
        this._resolveCurrentTask = finishTask

        // 重置标志位
        this.imageFetched = false
        this.ws = new WebSocket(`${getWebSocketUrl()}/ws?clientId=${this.clientId}`)
        const wsTimeout = setTimeout(() => {
          Message.warning('网络连接超时,请重新尝试!')
          this.ws.close()
          finishTask()
        }, 2000)
        this.ws.onopen = () => {
          console.log('✅ WebSocket 连接成功')
          // 记录任务开始
          getTaskStart({
            type: Product_Retouch,
            prompt: taskPrompt || '',
            input_images: taskImages || []
          }).then(rep => {
            this.taskLogId = rep.data.task.id
          })
          clearTimeout(wsTimeout)
        }
        this.ws.onmessage = async (event) => {
          clearTimeout(wsTimeout)
          const data = event.data
          if (typeof data === 'string') {
            try {
              const msg = JSON.parse(data)
              // 过滤掉监控消息，只显示关键信息
              if (msg.type !== 'crystools.monitor') {
                console.log('📡 收到消息:', msg)
              }

              if (msg.type === 'executing') {
                const execData = msg.data
                if (execData.node === null && execData.prompt_id === promptId) {
                  console.log('✅ 产品精修完成')
                  this.currentExecutingNode = ''
                  this.$emit('nodeUpdate', '')
                  this.ws.close()
                  // 防止重复调用
                  if (!this.imageFetched) {
                    this.imageFetched = true
                    await this.fetchResultImage(promptId)
                  }
                  finishTask()
                } else if (execData.node !== null) {
                  console.log(`🔄 正在执行节点: ${execData.node}`)
                  // 更新当前执行的节点名称
                  const nodeName = this.nodeNameMap[execData.node] || `执行节点 ${execData.node}`
                  this.currentExecutingNode = nodeName
                  this.$emit('nodeUpdate', nodeName)
                }
              } else if (msg.type === 'progress') {
                const value = msg.data.value
                const max = msg.data.max
                const current = (value / max * 100).toFixed(2)
                console.log(`📈 进度: ${current}%`)
                // 发送进度给父组件
                this.$emit('progressUpdate', current)
              } else if (msg.type === 'status') {
                console.log('📊 状态更新:', msg.data)
                // 如果队列为空且没有执行中的任务，说明任务已经完成或跳过
                if (msg.data.status && msg.data.status.exec_info) {
                  const execInfo = msg.data.status.exec_info
                  if (execInfo.queue_remaining === 0) {
                    console.log('🤔 队列为空，检查任务状态...')
                    // 等待2秒后检查任务状态
                    setTimeout(async () => {
                      await this.checkTaskStatus(promptId)
                    }, 2000)
                  }
                }
              } else if (msg.type === 'execution_error') {
                // 展开输出后端返回的所有错误细节，避免被控制台折叠成 Object
                const errData = msg.data || {}
                console.group('❌ 产品精修执行错误（来自 ComfyUI 后端节点）')
                console.error('节点ID (node_id):', errData.node_id)
                console.error('节点类型 (node_type):', errData.node_type)
                console.error('异常类型 (exception_type):', errData.exception_type)
                console.error('异常信息 (exception_message):', errData.exception_message)
                console.error('已执行节点 (executed):', errData.executed)
                console.error('当前节点输入 (current_inputs):', errData.current_inputs)
                console.error('当前节点输出 (current_outputs):', errData.current_outputs)
                console.error('堆栈 (traceback):', Array.isArray(errData.traceback) ? errData.traceback.join('\n') : errData.traceback)
                console.error('完整原始数据 (raw):', JSON.stringify(errData, null, 2))
                console.groupEnd()

                // 取一段更友好的提示信息展示给用户
                const friendlyMsg = errData.exception_message || errData.exception_type || '产品精修过程中出现错误'
                // 添加失败的 AI 回复到历史
                this.productChatHistory.push({
                  role: 'assistant',
                  content: '抱歉，似乎发生了某些问题：' + friendlyMsg,
                  timestamp: new Date().toISOString(),
                  isError: true // 标记为错误消息
                })
                this.$emit('chatUpdate', this.productChatHistory)
                Message.error('产品精修执行失败：' + friendlyMsg)
                this.ws.close()
                finishTask()
              }
            } catch (error) {
              console.error('WebSocket消息解析失败:', error)
            }
          }
        }
        this.ws.onclose = async () => {
          console.log('🔌 WebSocket 连接关闭')
          try {
            const index = await cudaIndexNum()
            if (index) {
              await cuda.changeCudaCurrentStatus(index, 'leisure')
            }
          } catch (error) {
            console.log(error)
          }
          // 如果还未 finish（如服务端意外关闭），在这里兑现 Promise
          finishTask()
        }
        this.ws.onerror = (error) => {
          console.error('❌ WebSocket 错误:', error)
          Message.error('WebSocket 连接错误')
          finishTask()
        }
      })
    },
    // 检查任务状态
    async checkTaskStatus(promptId) {
      try {
        console.log('🔍 检查任务状态:', promptId)
        const historyRequest = axios.create({
          baseURL: getLastUsedBaseUrl(),
          timeout: 10000,
          headers: {
            'Content-Type': 'application/json'
          }
        })
        const response = await historyRequest.get(`/history/${promptId}`)
        const promptInfo = response.data[promptId]
        console.log('📝 任务历史信息:', promptInfo)

        if (!promptInfo) {
          console.error('❌ 找不到任务信息，任务可能还在排队中...')
          return
        }

        // 检查任务状态
        if (promptInfo.status) {
          console.log('📊 任务状态:', promptInfo.status.status_str)

          if (promptInfo.status.status_str === 'success') {
            // 产品精修使用OSS节点上传，outputs可能为空，直接尝试获取图片
            console.log('✅ 任务已完成，尝试获取图片URL')
            // 防止重复调用
            if (!this.imageFetched) {
              this.imageFetched = true
              await this.fetchResultImage(promptId)
            }
            // 批次任务推进
            if (this._resolveCurrentTask) this._resolveCurrentTask()
          } else if (promptInfo.status.status_str === 'error') {
            console.error('❌ 任务执行失败:', promptInfo.status)
            Message.error('任务执行失败，请检查工作流配置')
            if (this.ws) this.ws.close()
            // 批次任务推进
            if (this._resolveCurrentTask) this._resolveCurrentTask()
          }
        }
      } catch (error) {
        console.error('🚫 检查任务状态失败:', error)
      }
    },
    // 记录任务结束
    async recordTaskEnd(imageUrl) {
      try {
        const imgInfo = await getImageSizeByLoading(imageUrl)
        const info = {
          details: [{ path: imageUrl, imgInfo: imgInfo }]
        }
        await getTaskEnd({ result: JSON.stringify(info) }, this.taskLogId)
      } catch (error) {
        console.error('记录任务结束失败:', error)
      }
    },
    // 获取结果图片URL
    async fetchResultImage(promptId) {
      try {
        const historyRequest = axios.create({
          baseURL: getLastUsedBaseUrl(),
          timeout: 5000,
          headers: {
            'Content-Type': 'application/json'
          }
        })
        const response = await historyRequest.get(`/history/${promptId}`)
        const promptInfo = response.data[promptId]
        console.log('📄 完整的promptInfo:', promptInfo)
        if (promptInfo.status.status_str === 'success') {
          // 从当前任务对应的 OSS 上传节点读取输出 filename
          const promptIndex = 2 // prompt 信息的下标
          const nodeId = this._currentOssNodeId || '8' // 常规类: '8'，特殊类: '7'
          console.log('🔍 尝试访问路径:', `promptInfo.prompt[${promptIndex}][${nodeId}]`)
          // 添加安全检查
          if (!promptInfo.prompt || !promptInfo.prompt[promptIndex] || !promptInfo.prompt[promptIndex][nodeId]) {
            console.error(`历史记录结构异常，无法找到节点${nodeId}的数据`)
            console.error('prompt数组:', promptInfo.prompt)
            // 添加失败的 AI 回复到历史
            this.productChatHistory.push({
              role: 'assistant',
              content: '抱歉，似乎发生了某些问题',
              timestamp: new Date().toISOString(),
              isError: true // 标记为错误消息
            })
            this.$emit('chatUpdate', this.productChatHistory)
            Message.error('获取生成结果失败：历史记录结构异常')
            this.processing = false
            this.$emit('aiTyping', false)
            return
          }
          // 从prompt中解析filename
          const filenameStr = promptInfo.prompt[promptIndex][nodeId].inputs.filename
          console.log('🔍 原始 filename 字符串:', filenameStr)
          const tmp = JSON.parse(filenameStr)
          console.log('✅ 解析的文件名:', tmp)
          // 生成完整OSS URL
          const imageUrl = genOssImage(tmp[0])
          console.log('✅ 生成的图片URL:', imageUrl)
          // 添加AI回复到历史
          this.productChatHistory.push({
            role: 'assistant',
            content: '产品精修完成！',
            resultImage: imageUrl,
            timestamp: new Date().toISOString()
          })
          this.$emit('chatUpdate', this.productChatHistory)
          this.$emit('aiTyping', false)
          this.processing = false
          // 记录任务结束
          await this.recordTaskEnd(imageUrl)
          Message.success('产品精修成功!')
        } else {
          console.error('任务状态异常:', promptInfo.status)
          const errorMsg = `任务执行失败: ${promptInfo.status.status_str}`
          // 添加失败的 AI 回复到历史
          this.productChatHistory.push({
            role: 'assistant',
            content: '抱歉，似乎发生了某些问题',
            timestamp: new Date().toISOString(),
            isError: true // 标记为错误消息
          })
          this.$emit('chatUpdate', this.productChatHistory)
          Message.error(errorMsg)
          this.processing = false
          this.$emit('aiTyping', false)
        }
      } catch (error) {
        console.error('获取图片失败：', error)
        console.error('错误详情:', error.response?.data || error.message)
        // 添加失败的 AI 回复到历史
        this.productChatHistory.push({
          role: 'assistant',
          content: '抱歉，似乎发生了某些问题',
          timestamp: new Date().toISOString(),
          isError: true // 标记为错误消息
        })
        this.$emit('chatUpdate', this.productChatHistory)
        Message.error('获取生成结果失败，请查看控制台')
        this.processing = false
        this.$emit('aiTyping', false)
      }
    }
  }
}
</script>

<style lang="less" scoped>
@import "@/style/common.less";
@import "@/assets/fonts/style.css";
@import "@/assets/font_sczlgzino/iconfont.css";

* {
  margin: 0;
  padding: 0;
  box-sizing: border-box;
}

html {
  font-family: "icomoon";
  overflow: hidden;
}

.iconfont {
  font-family: "iconfont" !important;
  font-size: 25px;
  font-style: normal;
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
}

.active {
  background: @basicselect;
}

.navigation {
  position: absolute;
  width: calc(100vw - 80px);
  bottom: 15px;
  left: 100px;
  z-index: 8;
  transition: all 0.5s;

  .navigation_bottom {
    position: relative;
    width: 1536px;
    height: 50px;
    font-size: 23px;
    margin: 0 auto;
    justify-content: space-between;
    margin-bottom: 10px;

    .ai_assistant {
      position: absolute;
      bottom: -10px;
      width: 120px;
      height: 120px;
      opacity: 0;
      transition: all 0.5s;
    }

    .scroll_bottom {
      position: absolute;
      right: 0px;
      font-size: 20px;
      color: @font_white;
      background-color: #c1c1c1;
      padding: 10px 15px;
      cursor: pointer;
      border-radius: @border_radius_3;
      transition: all 0.5s;
      opacity: 0;
    }
  }

  .navigation_items {
    position: relative;
    width: 1336px;
    height: 220px;
    display: flex;
    flex-direction: column;
    background: rgba(235, 235, 235, 0.5);
    backdrop-filter: blur(10px);
    border: 1px solid @font_white;
    border-radius: @border_radius;
    transition: all 0.3s;
    margin: 0 auto;
    box-shadow: 0 0 10px 0px rgba(0, 0, 0, 0.2);

    .main_input {
      width: 100%;
      height: 220px;
      display: flex;
      flex-direction: column;
      padding: 15px 20px;
      gap: 10px;
      transition: all 0.3s;

      // 拖拽悬浮样式
      &.drag-over {
        background: #fff3e8;
        box-shadow: inset 0 0 0 2px @morejoy;
      }

      // 已上传图片缩略图区域
      .uploaded-thumbnails {
        display: flex;
        flex-wrap: nowrap;
        gap: 8px;
        overflow-x: auto;
        padding: 4px 0;
        flex-shrink: 0;
        height: 68px;

        &::-webkit-scrollbar {
          height: 4px;
        }

        &::-webkit-scrollbar-thumb {
          background: #d0d0d0;
          border-radius: 2px;
        }

        .thumbnail-item {
          position: relative;
          width: 60px;
          height: 60px;
          border-radius: 8px;
          overflow: hidden;
          flex-shrink: 0;
          border: 2px solid #e5e7eb;
          transition: all 0.3s;

          &:hover {
            border-color: @morejoy;
            transform: scale(1.05);
          }

          .thumbnail-img {
            width: 100%;
            height: 100%;
            object-fit: cover;
          }

          .remove-btn {
            position: absolute;
            top: 2px;
            right: 2px;
            width: 16px;
            height: 16px;
            background: rgba(0, 0, 0, 0.6);
            color: white;
            border-radius: 50%;
            display: flex;
            align-items: center;
            justify-content: center;
            font-size: 12px;
            cursor: pointer;
            transition: all 0.3s;
            line-height: 1;

            &:hover {
              background: rgba(255, 0, 0, 0.8);
              transform: scale(1.1);
            }
          }
        }
      }

      // 输入框区域
      .input-area {
        flex: 1;
        display: flex;
        align-items: center;
        gap: 15px;
        position: relative;
        min-height: 60px;

        textarea {
          flex: 1;
          height: 100%;
          min-height: 60px;
          font-family: '微软雅黑';
          font-size: 23px;
          padding: 10px 20px;
          padding-right: 80px;
          border: none;
          background: none;
          resize: none;
          outline: none;
        }

        .sent {
          position: absolute;
          right: 0;
          width: 60px;
          height: 60px;
          text-align: center;
          line-height: 60px;
          font-size: 35px;
          color: @font_white;
          border: none;
          border-radius: 30px;
          background-color: @font_gray;
          cursor: pointer;
          opacity: 0.7;
          transition: all 0.3s;

          &.has_input {
            background-color: @morejoy;
            opacity: 1;
          }

          &:hover:not(:disabled) {
            opacity: 1;
            font-size: 38px;
          }

          &:disabled {
            opacity: 0.3;
            cursor: not-allowed;
          }

          .rotating {
            animation: rotate 1s linear infinite;
          }
        }
      }

      // 底部按钮区域
      .input-options {
        display: flex;
        align-items: center;
        gap: 10px;
        flex-shrink: 0;
        height: 40px;

        // 上传按钮
        .option-btn {
          position: relative;
          display: flex;
          align-items: center;
          gap: 5px;
          padding: 8px 12px;
          background: white;
          border: 1px solid #d0d0d0;
          border-radius: 8px;
          cursor: pointer;
          transition: all 0.3s;
          user-select: none;
          font-size: 13px;
          color: #333;
          font-weight: 500;

          &:hover {
            border-color: @morejoy;
            background: #fff8f3;
            color: @morejoy;
          }
        }

        // 图像大小下拉
        .option-dropdown {
          position: relative;
          display: flex;
          align-items: center;
          gap: 5px;
          padding: 8px 12px;
          background: white;
          border: 1px solid #d0d0d0;
          border-radius: 8px;
          cursor: pointer;
          transition: all 0.3s;
          user-select: none;

          &:hover {
            border-color: @morejoy;
            background: #fff8f3;
          }

          .option-label {
            font-size: 12px;
            color: #999;
            font-weight: 400;
          }

          .option-value {
            font-size: 13px;
            color: #333;
            font-weight: 500;
          }

          .arrow {
            font-size: 12px;
            color: #999;
            transition: transform 0.3s;
          }

          .dropdown-menu {
            position: absolute;
            bottom: calc(100% + 5px);
            left: 0;
            min-width: 100%;
            background: white;
            border: 1px solid #e0e0e0;
            border-radius: 8px;
            box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
            z-index: 100;
            overflow: hidden;

            .menu-item {
              padding: 10px 15px;
              font-size: 13px;
              color: #333;
              cursor: pointer;
              transition: all 0.2s;
              white-space: nowrap;

              &:hover {
                background: #fff8f3;
                color: @morejoy;
              }

              &.active {
                background: #fff8f3;
                color: @morejoy;
                font-weight: 500;

                &::after {
                  content: '✓';
                  float: right;
                  font-weight: bold;
                  margin-left: 8px;
                }
              }
            }
          }
        }
      }
    }

    /* 放大后的发送按钮（绝对定位） */
    .sent_expanded {
      position: absolute;
      right: 15px;
      top: 50%;
      transform: translateY(-50%);
      width: 60px;
      height: 60px;
      text-align: center;
      line-height: 60px;
      font-size: 35px;
      color: @font_white;
      border: none;
      border-radius: 30px;
      background-color: @font_gray;
      cursor: pointer;
      opacity: 0.7;
      transition: all 0.3s;
      z-index: 10;

      &.has_input {
        background-color: @morejoy; // 有输入时变成橙色
        opacity: 1;
      }

      &:hover:not(:disabled) {
        opacity: 1;
        font-size: 38px;
      }

      &:disabled {
        opacity: 0.3;
        cursor: not-allowed;
      }

      .rotating {
        animation: rotate 1s linear infinite;
      }
    }
  }
}

@keyframes rotate {
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
}
</style>
